import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import styled, { ThemeContext } from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../contexts/basket/useBasketContext';
import useSiteContext from '../../../contexts/site/useSiteContext';
import useOrderContext from "../../../contexts/order/useOrderContext";
import useOperatorContext from "../../../contexts/operator/useOperatorContext";
import PageSpinnerThree from '../../general/PageSpinnerThree';
import AppLayout from '../../layouts/app/AppLayout';
import Basket from '../Menu/components/basket/Basket';
import CustomerForm from './components/CustomerForm';
import ScrollToTop from '../../../hooks/scrollToTop';
import { Elements as StripeElementProvider } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import AgeRestrictionModal from "./components/AgeRestrictionModal";
import { toItems, track } from '../../../services/analytics';

const CheckoutPage = () => {
    const navigate = useNavigate();
    const operatorContext = useOperatorContext();
    const siteContext = useSiteContext();
    const basketContext = useBasketContext();
    const orderContext = useOrderContext();
    const themeContext = useContext(ThemeContext);

    const stripeOptions = useMemo<StripeElementsOptions>(() => ({
        fonts: [{
            family: 'OSSecondaryFont',
            src: `url(${themeContext.v4.secondary_font_regular})`
        }]
    }), [themeContext.v4.secondary_font_regular]);

    const stripePromise = useMemo(() => {
        const key = operatorContext.operator?.allow_stripe_test
            ? process.env.REACT_APP_STRIPE_KEY_TEST
            : process.env.REACT_APP_STRIPE_KEY;
        return loadStripe(key ?? '');
    }, [operatorContext.operator?.allow_stripe_test]);

    const [ageModalShow, setAgeModalShow] = useState(false);

    useEffect(() => {
        if (! basketContext.validatedBasket?.basket ) {
            navigate('/');
        } else {
            track('begin_checkout', { channel: siteContext.channel, site_handle: basketContext.validatedSite?.handle }, {
                ecommerce: {
                    value: (basketContext.validatedBasket.total ?? 0) / 100,
                    items: toItems(basketContext.validatedBasket.basket),
                },
            });

            if ( basketContext.validatedBasket.has_age_restricted_item ) {
                setAgeModalShow(basketContext.validatedBasket.has_age_restricted_item);
                return;
            }
        }
    }, []);

    if (!siteContext || !basketContext || basketContext.validatedBasket === undefined) {
        return <PageSpinnerThree show={true} />;
    }

    if (siteContext.loading || basketContext.loading) {
        return <PageSpinnerThree show={true} />;
    }

    if (!operatorContext || !operatorContext.operator || !siteContext) {
        return <PageSpinnerThree show={true} />;
    }

    if (operatorContext.loading || siteContext.loading) {
        return <PageSpinnerThree show={true} />;
    }

    return (
        <AppLayout page="checkout">
            <StripeElementProvider stripe={stripePromise} options={stripeOptions}>
                <ScrollToTop />
                <Helmet>
                    <title>{operatorContext.operator?.name} - {t`Checkout - Powered by Orderswift`}</title>
                </Helmet>
                <AgeRestrictionModal
                    show={ageModalShow}
                    onHide={() => setAgeModalShow(false)}
                />
                <StyledContainer>
                    <StyledHeaderRow>
                        <h1 className="page-header">Checkout</h1>
                    </StyledHeaderRow>
                    <StyledRow>
                        <StyledOrderDetails>
                            <Basket checkout={true} reset={false} site={basketContext.validatedSite} />
                        </StyledOrderDetails>
                        <StyledPayment>
                            <CustomerForm />
                        </StyledPayment>
                    </StyledRow>
                </StyledContainer>
            </StripeElementProvider>
        </AppLayout>
    );
};

const StyledContainer = styled.div`
    max-width: 1092px;
    display: block;
    margin-left: auto;
    margin-right: auto;

    @media screen and (min-width: 993px) {
        padding: 0 20px;
    }
`;

const StyledHeaderRow = styled.div`
    padding: 24px 0;
    text-align: center;
`;

const StyledRow = styled.div`
    @media screen and (min-width: 993px) {
        padding-bottom: 24px;
        display: flex;
        gap: 56px;
        align-items: flex-start;
    }
`;

const StyledOrderDetails = styled.div`
    align-self: flex-start;
    width: 100%;
    background: var(--layouts_container_bg);

    @media screen and (min-width: 993px) {
        margin-bottom: 16px;
        order: 2;
        max-width: 600px;
        border: 1px solid var(--layouts_container_border);
        border-radius: var(--radius_xl);
        position: sticky;
        top: 112px;
    }
`;

const StyledPayment = styled.div`
    width: 100%;
    background: var(--layouts_container_bg);

    @media screen and (min-width: 993px) {
        order: 1;
        max-width: 600px;
        border: 1px solid var(--layouts_container_border);
        border-radius: var(--radius_xl);
    }
`;

export default CheckoutPage;
